/**
 * Task-category vocabulary (WS-7) — the frontend resolver, the sibling of
 * api/_shared/taskCategories.ts (same defensive walk, same platform
 * default, kept in lockstep by the paired unit suites). Mirrors the
 * readRateBands seam (src/lib/notes.ts, #456).
 *
 * The vocabulary lives in tenants.config `workspace.task_categories`
 * (array of { value, label } with non-empty strings), delivered by
 * /getTenantConfig (useTenantConfig). Malformed/absent config falls back
 * to TASK_CATEGORIES — never a blank picker. The server validates writes
 * against the same set (repos/workItems.ts); work_items.category
 * deliberately has NO DB CHECK (a CHECK would fight tenant config).
 */

export interface TaskCategoryOption {
  value: string;
  label: string;
}

/** Platform default (mirrors api/_shared/taskCategories.ts DEFAULT_TASK_CATEGORIES). */
export const TASK_CATEGORIES: readonly TaskCategoryOption[] = [
  { value: 'billing', label: 'Billing' },
  { value: 'clinical', label: 'Clinical' },
  { value: 'intake', label: 'Intake' },
  { value: 'admin', label: 'Admin' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'system', label: 'System' },
  { value: 'other', label: 'Other' },
];

/**
 * Read the tenant's task-category options from the tenants.config blob.
 * Defensive walk (readRateBands precedent): only well-formed entries are
 * kept; anything short of a non-empty list falls back to the platform
 * default. Pure; unit-tested (tenantConfigSeams).
 */
export function readTaskCategories(config: unknown): readonly TaskCategoryOption[] {
  if (typeof config !== 'object' || config === null) return TASK_CATEGORIES;
  const workspace = (config as Record<string, unknown>).workspace;
  if (typeof workspace !== 'object' || workspace === null) return TASK_CATEGORIES;
  const v = (workspace as Record<string, unknown>).task_categories;
  if (!Array.isArray(v)) return TASK_CATEGORIES;
  const categories: TaskCategoryOption[] = [];
  for (const entry of v) {
    if (typeof entry !== 'object' || entry === null) continue;
    const value = (entry as Record<string, unknown>).value;
    const label = (entry as Record<string, unknown>).label;
    if (
      typeof value === 'string' &&
      value.trim().length > 0 &&
      typeof label === 'string' &&
      label.trim().length > 0
    ) {
      categories.push({ value: value.trim(), label: label.trim() });
    }
  }
  return categories.length > 0 ? categories : TASK_CATEGORIES;
}

/** Display label for a stored category value; the raw value when the
 *  tenant's vocabulary no longer names it (an edited config must never
 *  blank an existing chip). */
export function taskCategoryLabel(
  categories: readonly TaskCategoryOption[],
  value: string | null,
): string | null {
  if (value === null) return null;
  return categories.find((c) => c.value === value)?.label ?? value;
}


/**
 * Category chip styling (founder 2026-07-10: "category colors need
 * differences"). CATEGORICAL hue, deliberately outside the status
 * registers (gold stays awaiting-confirm, pepper stays attention,
 * tick-affirm stays done): lightness-differentiated pairs from the
 * brand's identity families. Off-vocabulary (grandfathered) values read
 * sand, like Other. FE-only presentation; the api mirror carries the
 * vocabulary, never styling.
 */
const TASK_CATEGORY_CHIP_CLASSES: Record<string, string> = {
  billing: 'bg-warm-100 text-warm-700',
  clinical: 'bg-sage-50 text-sage-600',
  intake: 'bg-heather text-heather-deep',
  admin: 'bg-sage-100 text-sage-800',
  follow_up: 'bg-tick-affirm text-affirm-deep',
  system: 'bg-cream text-ink-soft',
  other: 'bg-sand text-ink-soft',
};

export function taskCategoryChipClass(value: string | null): string {
  if (value === null) return TASK_CATEGORY_CHIP_CLASSES.other!;
  return TASK_CATEGORY_CHIP_CLASSES[value] ?? TASK_CATEGORY_CHIP_CLASSES.other!;
}
